import { Head, Link, router, usePage } from '@inertiajs/react'
import { Reveal, RevealSequence } from '@/Components/vip-card/reveal'
import { Sphere } from '@/Components/vip-card/sphere'
import { useSafeTranslation } from '@/hooks/use-safe-translation'
import { cn } from '@/lib/utils'
import type { PageProps } from '@/types'

type Action = 'home' | 'contact' | 'retry'

/** What each error offers, the main one first. A page that is wrong for the visitor sends them on; one that may pass invites another try. */
const ACTIONS: Record<number, Action[]> = {
  401: ['home', 'contact'],
  402: ['home', 'contact'],
  403: ['home', 'contact'],
  404: ['home', 'contact'],
  419: ['retry', 'home'],
  429: ['retry', 'home'],
  500: ['retry', 'home'],
  503: ['retry'],
}

const BUTTON =
  'flex h-12 items-center justify-center rounded-full px-6 text-base font-semibold transition-colors duration-300'

/**
 * An error, as a page of the site.
 *
 * It is one of the app's own pages, so the header, the logo and its buttons
 * move around the site the way every other page's do.
 */
export default function Error() {
  const { status } = usePage<PageProps & { status: number }>().props
  const t = useSafeTranslation()
  const title = t(`errors.${status}.title`)

  return (
    <div className="vip-theme contents">
      <div aria-hidden className="fixed inset-0 -z-10 bg-background" />

      <Head title={title} />

      <RevealSequence>
        <div className="flex flex-col items-center gap-10 py-6 text-center lg:py-12">
          <Reveal
            aria-hidden
            className="relative flex aspect-[436/260] w-full max-w-[436px] items-center justify-center"
          >
            <Sphere name="sphere-lg" className="left-[52%] top-0 w-[44%]" />
            <Sphere name="sphere-sm" className="bottom-0 left-[12%] w-[24%]" />

            <span
              dir="ltr"
              className="relative text-[112px] font-extrabold leading-none tracking-tight text-foreground sm:text-[160px]"
            >
              {status}
            </span>
          </Reveal>

          <Reveal order={1} className="flex max-w-[516px] flex-col items-center gap-4">
            <span className="rounded-full bg-primary/[0.06] px-3.5 py-1.5 text-base font-semibold leading-6 text-primary dark:bg-primary/[0.22]">
              {t('errors.label')} {status}
            </span>

            <h1 className="text-[32px] font-extrabold leading-tight text-foreground sm:text-[44px]">
              {title}
            </h1>

            <p className="text-lg leading-7 text-muted-foreground">
              {t(`errors.${status}.description`)}
            </p>
          </Reveal>

          <Reveal
            order={2}
            className="flex w-full flex-col items-stretch justify-center gap-3 sm:w-auto sm:flex-row"
          >
            {(ACTIONS[status] ?? ACTIONS[404]).map((action, index) => {
              const look = cn(
                BUTTON,
                index === 0
                  ? 'bg-primary text-white hover:bg-primary/90'
                  : 'bg-card text-muted-foreground ring-1 ring-border hover:text-foreground'
              )

              return action === 'retry' ? (
                <button key={action} type="button" onClick={() => router.reload()} className={look}>
                  {t('errors.retry')}
                </button>
              ) : (
                <Link
                  key={action}
                  href={action === 'contact' ? '/contact-us' : '/'}
                  className={look}
                >
                  {t(`errors.${action}`)}
                </Link>
              )
            })}
          </Reveal>
        </div>
      </RevealSequence>
    </div>
  )
}
